
import { computed, onMounted, ref, defineComponent } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { game } from '../apis/game';
import { GamesList, GameStatusResult, ListByGameTypeResult } from '../types/game';
import GameNavbar from '../components/navbar/GameNavbar.vue';

import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { useAuthStore } from '../stores/auth';
import { storeToRefs } from 'pinia';
import { showVuePopup } from '../utils/showVuePopup';
import Login from '../popup/Login.vue';
import { useToast } from 'primevue/usetoast';
import { openGameUrl } from '../utils/openGameUrl';
import { isHiddenProduct } from '../utils/site-config';

export default defineComponent({
	name: 'Lotto',
	components: {
		IconField,
		InputIcon,
		InputText,
		Button,
		Message,
		GameNavbar,
	},
	// ถ้าอยากให้กำหนด gameType จากภายนอก (เช่น lotto = 9) ก็รับเป็น prop แบบนี้
	props: {
		gameType: {
			type: Number,
			required: true,
		},
	},
	setup(props) {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		const toast = useToast();

		const error = ref<string | null>(null);
		const loadingProducts = ref(true);

		const gameState = ref<GameStatusResult>({
			card: false,
			casino: false,
			cock: false,
			esport: false,
			fish: false,
			keno: false,
			lotto: false,
			poker: false,
			slot: false,
			sport: false,
			trade: false,
		});

		const selectedProduct = ref<GamesList | null>(null);
		const products = ref<GamesList[]>([]);

		const searchQuery = ref('');

		const productsList = computed(() => {
			const q = searchQuery.value.toLowerCase();
			return products.value.filter((product) =>
				product.productName?.toLowerCase().includes(q)
			);
		});

		const handleGameStatus = async () => {
			try {
				const resp = await game.status();

				if (resp.code !== '0') throw new Error(resp.message);

				gameState.value = resp.result as GameStatusResult;
			} catch (err: any) {
				error.value = err.message ?? 'ไม่สามารถโหลดสถานะเกมได้';
			}
		};

		const handleProducts = async () => {
			loadingProducts.value = true;
			error.value = null;

			try {
				const resp = await game.recommended();

				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as ListByGameTypeResult;

				products.value = (result.gamesList || []).filter(
					(product) => !isHiddenProduct(product)
				);
				selectedProduct.value = products.value[0] ?? null;
			} catch (err: any) {
				error.value = err.message ?? 'ไม่สามารถโหลดรายการเกมได้';
			} finally {
				loadingProducts.value = false;
			}
		};

		const handleFavoriteAdd = async (code: string) => {
			try {
				const resp = await game.favoriteAdd(code);

				if (resp.code !== '0') throw new Error(resp.message);

				toast.add({
					severity: 'success',
					summary: resp.message,
					detail: 'เพิ่มลงรายการโปรดสำเร็จ',
					life: 3000,
				});
			} catch (err: any) {
				error.value = err.message ?? 'เพิ่มรายการโปรดไม่สำเร็จ';
			}
		};

		const handleFavoriteRemove = async (code: string) => {
			try {
				const resp = await game.favoriteDelete(code);

				if (resp.code !== '0') throw new Error(resp.message);

				toast.add({
					severity: 'success',
					summary: resp.message,
					detail: 'ลบรายการโปรดสำเร็จ',
					life: 3000,
				});
			} catch (err: any) {
				error.value = err.message ?? 'ลบรายการโปรดไม่สำเร็จ';
			}
		};

		const clickGame = async (code: string) => {
			if (!auth.isLoggedIn) {
				await showVuePopup(Login, {}, {
					customClass: {
						popup:
							'bg-[url(https://topgame-storage.sgp1.cdn.digitaloceanspaces.com/theme/bg-login.webp)] bg-size-[110%_100%] bg-center bg-transparent',
					},
				});
				return;
			}

			// หาเกมจาก products ปัจจุบัน (รวมผลค้นหาแล้วอยู่ใน productsList)
			const found =
				products.value.find((g) => g.loginCode === code) ?? null;

			if (!found) {
				error.value = 'ไม่พบข้อมูลเกมที่เลือก';
				return;
			}

			try {
				const payload = {
					gameType: props.gameType,
					productCode: found.productCode,
					code,
					redirect: window.location.origin,
					isMobile: true,
				};

				const resp = await game.login(payload);

				if (resp.code !== '0') throw new Error(resp.message);

				openGameUrl(resp?.url || window.location.origin, (target) => window.location.replace(target));
			} catch (err: any) {
				error.value = err.message ?? 'ไม่สามารถเข้าเกมได้';
			}
		};

		const clickBtnFavorite = async (code: string, isFavorite: boolean) => {
			try {
				if (isFavorite) {
					await handleFavoriteRemove(code);
				} else {
					await handleFavoriteAdd(code);
				}
			} catch (err: any) {
				error.value = err.message ?? 'จัดการรายการโปรดไม่สำเร็จ';
			} finally {
				// reload list ให้สถานะ isFavorite อัปเดต
				await handleProducts();
			}
		};

		onMounted(async () => {
			if (!auth.isLoggedIn) {
				window.location.href = '/';
				return;
			}

			await handleGameStatus();
			await handleProducts();
		});

		return {
			auth,
			productsList,
			searchQuery,
			selectedProduct,
			clickGame,
			clickBtnFavorite,
			error,
			loadingProducts,
			gameState,
		};
	},
});
